import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Link, NavLink, Outlet, useNavigate } from "react-router";
import { APP_BUILD, APP_VERSION } from "../lib/changelog.ts";
import { PERSONAL, useCurrentTeam } from "../lib/current-team.tsx";
import { useMe, useSignOut, useTeams } from "../lib/queries.ts";
import { Avatar } from "./Avatar.tsx";
import { Icon } from "./Icon.tsx";
import { Logo } from "./Logo.tsx";
import { StuffDrawerProvider } from "./StuffDrawer.tsx";

const NAV = [
  { to: "/library", key: "library", ready: true },
  { to: "/live", key: "live", ready: true },
  { to: "/matches", key: "matches", ready: true },
  { to: "/configs", key: "configs", ready: true },
  { to: "/leaderboard", key: "leaderboard", ready: true },
  { to: "/team", key: "team", ready: true },
] as const;

export function AppShell() {
  const { t } = useTranslation();
  const meData = useMe().data;
  const me = meData?.user;
  const teams = useTeams().data ?? [];
  const { team, select } = useCurrentTeam();
  const signOut = useSignOut();
  const navigate = useNavigate();
  const qc = useQueryClient();

  return (
    <div className="app">
      <header className="app-header">
        <Link to="/library" className="app-logo" aria-label={t("brand.name")}>
          <Logo />
          <span className="app-name" aria-hidden="true">
            Companion
          </span>
        </Link>
        <nav aria-label={t("nav.main")} className="app-nav">
          {NAV.map((item) => (
            <NavLink key={item.key} to={item.to} className={({ isActive }) => `nav-link${isActive ? " is-active" : ""}${item.ready ? "" : " is-soon"}`}>
              {t(`nav.${item.key}`)}
              {!item.ready && <span className="soon-tag">{t("nav.soon")}</span>}
            </NavLink>
          ))}
          {meData?.isAdmin && (
            <NavLink to="/access" className={({ isActive }) => `nav-link${isActive ? " is-active" : ""}`}>
              {t("nav.access")}
            </NavLink>
          )}
          {meData?.isAdmin && (
            <NavLink to="/admin/callouts" className={({ isActive }) => `nav-link${isActive ? " is-active" : ""}`}>
              {t("nav.callouts")}
            </NavLink>
          )}
          {meData?.isAdmin && (
            <NavLink to="/admin/maps" className={({ isActive }) => `nav-link${isActive ? " is-active" : ""}`}>
              {t("nav.meshes")}
            </NavLink>
          )}
        </nav>
        <div className="app-header-end">
          {teams.length > 0 ? (
            <label className="team-switch">
              <span className="visually-hidden">{t("nav.switchTeam")}</span>
              <select value={team?.id ?? PERSONAL} onChange={(e) => select(e.target.value)}>
                {teams.map((x) => (
                  <option key={x.id} value={x.id}>
                    {x.name}
                  </option>
                ))}
                <option value={PERSONAL}>{t("nav.personal")}</option>
              </select>
            </label>
          ) : (
            <span className="team-chip">{t("nav.personal")}</span>
          )}
          <NavLink to="/settings" className={({ isActive }) => `btn btn-quiet btn-icon app-settings${isActive ? " is-active" : ""}`} aria-label={t("nav.settings")} title={t("nav.settings")}>
            <Icon name="gear" size={18} />
          </NavLink>
          {me && (
            <div className="user-chip">
              <Avatar name={me.name} url={me.avatarUrl} me size={30} />
              <span className="user-name">{me.name}</span>
              <button
                type="button"
                className="btn btn-quiet"
                onClick={() =>
                  signOut.mutate(undefined, {
                    onSuccess: () => {
                      qc.clear();
                      navigate("/sign-in", { replace: true });
                    },
                  })
                }
              >
                {t("nav.signOut")}
              </button>
            </div>
          )}
        </div>
      </header>
      <main className="app-main">
        <StuffDrawerProvider>
          <Outlet />
        </StuffDrawerProvider>
      </main>
      <footer className="app-footer small">
        <span>
          {t("brand.name")} v{APP_VERSION}
          {APP_BUILD && <span className="mono"> · {APP_BUILD}</span>}
        </span>
        <Link to="/changelog">{t("changelog.title")}</Link>
      </footer>
    </div>
  );
}
